import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Sparkles, AlertTriangle, ArrowRight, Play, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "")}/api`;

/**
 * Public investor demo gateway.
 * Validates the demo token, issues a short-lived read-only admin cookie via
 * /api/public/demo-login, sets a localStorage flag so the Admin UI shows a
 * demo banner + disables destructive actions, then redirects into /admin.
 *
 * This replaces the old slide walkthrough with a real clickable demo.
 */
export default function InvestorDemo() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const via = params.get("via") || "";
  const autoTour = params.get("tour") !== "0"; // default on
  const [status, setStatus] = useState("init"); // init | authed | error
  const [err, setErr] = useState("");
  const [fundInfo, setFundInfo] = useState(null);

  useEffect(() => {
    if (!token) { setStatus("error"); setErr("Missing demo token in URL."); return; }
    // Resolve fund info if a per-investor link id is present
    if (via) {
      axios.get(`${API}/public/investor-link/${encodeURIComponent(via)}`)
        .then((r) => { if (r.data?.ok) setFundInfo(r.data); }).catch(() => {});
      sessionStorage.setItem("dgx_attribution", JSON.stringify({ via }));
    }
    axios
      .post(`${API}/public/demo-login`, { token }, { withCredentials: true })
      .then(() => {
        localStorage.setItem("investorDemoMode", "1");
        if (via) localStorage.setItem("investorDemoVia", via);
        if (autoTour) localStorage.setItem("investorDemoAutoTour", "1");
        setStatus("authed");
      })
      .catch((e) => {
        setStatus("error");
        setErr(e.response?.status === 403 ? "Invalid or expired demo token." : "Unable to start demo session.");
      });
  }, [token, via, autoTour]);

  if (status === "error") {
    return (
      <div className="min-h-screen bg-[#050A07] flex items-center justify-center p-6" data-testid="investor-demo-error">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-10 h-10 text-red-400 mx-auto mb-4" />
          <h1 className="font-['Outfit'] text-xl font-bold text-white mb-2">Demo Unavailable</h1>
          <p className="text-[#A1B2A8] text-sm">{err}</p>
          <p className="text-[#A1B2A8]/50 text-xs mt-4">Expected URL format: <code className="text-[#22C55E]">/investor-demo?token=...</code></p>
          <a href="/" className="inline-block mt-6 text-[#22C55E] text-sm hover:underline">← Back to landing</a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050A07] text-white flex items-center justify-center" data-testid="investor-demo-gateway">
      <div className="max-w-xl w-full px-6">
        <div className="flex items-center gap-2 mb-4">
          <Sparkles className="w-4 h-4 text-[#22C55E]" />
          <span className="text-[#22C55E] text-[10px] font-semibold uppercase tracking-[0.3em]">
            {fundInfo ? `Personalized Demo · ${fundInfo.fund_name}` : "Investor Demo"}
          </span>
        </div>
        <h1 className="font-['Outfit'] text-4xl sm:text-5xl font-bold mb-4 leading-tight">
          {fundInfo?.investor_name ? <>Welcome, <span className="text-[#22C55E]">{fundInfo.investor_name.split(/\s+/)[0]}</span> from <span className="text-white">{fundInfo.fund_name}</span></>
            : <>Welcome to <span className="text-[#22C55E]">Delta GreenX</span></>}
        </h1>
        <p className="text-[#C5D5CC] text-base leading-relaxed mb-8">
          You&apos;re about to enter a live, read-only walkthrough of our operating infrastructure — not slides.
          You&apos;ll see real data for $825K of active capital flows, AI confidence scores, multi-source corporate
          intelligence profiles, supply-chain mapping, and the full returns lifecycle.
        </p>

        <div className="border border-[#1A2E22] bg-[#09120D] p-5 mb-6">
          <p className="text-[#A1B2A8] text-[10px] uppercase tracking-wider mb-3">What you can explore</p>
          <ul className="space-y-2 text-[#E8F3EC] text-sm">
            <li className="flex items-start gap-2"><span className="text-[#22C55E] mt-0.5">▸</span><span>Capital deployed, platform revenue realized & pipeline</span></li>
            <li className="flex items-start gap-2"><span className="text-[#22C55E] mt-0.5">▸</span><span>Deal-level AI Confidence Engine deep analysis</span></li>
            <li className="flex items-start gap-2"><span className="text-[#22C55E] mt-0.5">▸</span><span>Supply chain maps from exporter to ultimate buyer</span></li>
            <li className="flex items-start gap-2"><span className="text-[#22C55E] mt-0.5">▸</span><span>Multi-source corporate intelligence profiles · IN · US · UK · SG · AU</span></li>
            <li className="flex items-start gap-2"><span className="text-[#22C55E] mt-0.5">▸</span><span>Capital Velocity — how many times deployed capital rotates</span></li>
          </ul>
        </div>

        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={() => window.location.assign("/admin")}
            disabled={status !== "authed"}
            className="flex-1 bg-[#22C55E] hover:bg-[#1CAB4D] disabled:opacity-50 disabled:cursor-wait text-[#050A07] font-['Outfit'] font-semibold px-5 py-3 flex items-center justify-center gap-2 transition-colors"
            data-testid="enter-demo-btn"
          >
            {status === "authed" ? (
              <><Play className="w-4 h-4" /> Enter Live Demo <ArrowRight className="w-4 h-4" /></>
            ) : (
              <>Starting session…</>
            )}
          </button>
          <button
            onClick={() => { localStorage.setItem("investorDemoAutoTour", "0"); window.location.assign("/admin"); }}
            disabled={status !== "authed"}
            className="sm:flex-1 border border-[#1A2E22] hover:border-[#22C55E]/40 text-[#A1B2A8] hover:text-white px-5 py-3 flex items-center justify-center gap-2 text-sm transition-colors"
            data-testid="enter-demo-no-tour-btn"
          >
            <Eye className="w-4 h-4" /> Explore without guided tour
          </button>
        </div>

        <p className="text-[#A1B2A8]/40 text-[10px] mt-6 leading-relaxed">
          Read-only · 30-minute session · No login · All write actions are disabled.
          Powered by Delta GreenX's trade infrastructure.
        </p>
      </div>
    </div>
  );
}
